// ============================================================================
// CHAT（聊天）相关 Atoms
// ============================================================================

import { atom } from 'jotai'
import { atomFamily } from 'jotai/utils'

import { currentUserAtom } from '../user/AtomUsers'

const createChatsState = function () {
  // Unread counts atom family - one atom per user
  // The system uses server-side lastReadTimestamp tracking as the source of truth.
  // Client recalculates unread counts based on server's lastReadTimestamp when:
  // 1. Chat page loads (from server data)
  // 2. Messages arrive via WebSocket (increment locally)
  // 3. Read receipts arrive (recalculate from messages)
  const unreadCountsAtomFamily = atomFamily((userId) => {
    if (!userId) {
      return atom({})
    }
    return atom({})
  })

  // Chat rooms raw atom - stores raw chat rooms from server
  const chatRoomsRawAtom = atom([])

  // Conversations atom (not persisted, loaded from server)
  const chatRoomsAtom = atom([])

  // Selected room ID atom
  const selectedRoomIdAtom = atom(null)

  // Messages atom family - one atom per roomId
  // Each room has its own messages array
  const messagesAtomFamily = atomFamily((roomId) => {
    if (!roomId) {
      return atom([])
    }
    return atom([])
  })

  // User map atom - stores user info by userId
  const userMapAtom = atom({})

  // Loading state
  const loadingAtom = atom(true)

  // Error state
  const errorAtom = atom(null)

  // Connection state
  const connectionStateAtom = atom('disconnected')

  // Track which rooms have loaded history messages (not just WebSocket messages)
  // Uses Set to store room IDs that have loaded history
  const loadedHistoryRoomsAtom = atom(new Set())

  // Track last sync timestamp for each room (for resync after reconnect)
  // Format: { [roomId]: timestamp (ms) }
  const lastSyncTimestampsAtom = atom({})

  // Has unread messages atom - derived from unreadCountsAtomFamily
  // Returns true if there are any unread messages, false otherwise
  const hasUnreadMessagesAtom = atom((get) => {
    const currentUser = get(currentUserAtom)
    const currentUserId = currentUser?.resourceId
    if (!currentUserId) return false

    const unreadCountsAtom = unreadCountsAtomFamily(currentUserId)
    const unreadCounts = get(unreadCountsAtom)

    // Check if there are any unread messages
    return Object.values(unreadCounts).some((count) => count > 0)
  })

  // Total unread messages count atom - derived from unreadCountsAtomFamily
  // Returns the total number of unread messages across all rooms
  const totalUnreadCountAtom = atom((get) => {
    const currentUser = get(currentUserAtom)
    const currentUserId = currentUser?.resourceId
    if (!currentUserId) return 0

    const unreadCountsAtom = unreadCountsAtomFamily(currentUserId)
    const unreadCounts = get(unreadCountsAtom)

    // Sum all unread counts
    return Object.values(unreadCounts).reduce((total, count) => total + (count || 0), 0)
  })

  return {
    unreadCountsAtomFamily,
    chatRoomsRawAtom,
    chatRooms: chatRoomsAtom,
    selectedRoomId: selectedRoomIdAtom,
    messagesAtomFamily,
    userMap: userMapAtom,
    loading: loadingAtom,
    error: errorAtom,
    connectionState: connectionStateAtom,
    loadedHistoryRooms: loadedHistoryRoomsAtom,
    lastSyncTimestamps: lastSyncTimestampsAtom,
    hasUnreadMessages: hasUnreadMessagesAtom,
    totalUnreadCount: totalUnreadCountAtom,
  }
}

const chatsStateAtom = createChatsState()

export default chatsStateAtom
export const unreadCountsAtomFamily = chatsStateAtom.unreadCountsAtomFamily
export const chatRoomsRawAtom = chatsStateAtom.chatRoomsRawAtom
export const chatRoomsAtom = chatsStateAtom.chatRooms
export const selectedRoomIdAtom = chatsStateAtom.selectedRoomId
export const messagesAtomFamily = chatsStateAtom.messagesAtomFamily
export const userMapAtom = chatsStateAtom.userMap
export const chatLoadingAtom = chatsStateAtom.loading
export const chatErrorAtom = chatsStateAtom.error
export const connectionStateAtom = chatsStateAtom.connectionState
export const loadedHistoryRoomsAtom = chatsStateAtom.loadedHistoryRooms
export const lastSyncTimestampsAtom = chatsStateAtom.lastSyncTimestamps
export const hasUnreadMessagesAtom = chatsStateAtom.hasUnreadMessages
export const totalUnreadCountAtom = chatsStateAtom.totalUnreadCount
